import { useQuery } from '@tanstack/react-query'
import {
  formatMoney,
  formatPercent,
  type IncomeReport,
  type NetWorthReport,
} from '@greencandle/api-client'
import { api } from '../api'
import { Gap, Pending, StatCard, num, pct } from './primitives'
import { useNetWorth, useRegime } from './queries'

/**
 * The Overview tab's stat row: five passive readings, each auto-fetched.
 *
 * <p>Passive is the distinction that decides the shape. A reading says what is true now, so it
 * loads with the page; a proposal (the optimiser, the stress run) says what could be done and
 * stays behind a button, so that a target nobody asked for never appears on screen as though it
 * were fact. Everything in this file is the first kind.
 */

/**
 * ML-3, as a passive reading beside net worth and liabilities rather than inside the optimiser.
 *
 * <p>Auto-fetched like every other tile on this tab (`useQuery`, no button) - unlike the
 * optimiser panel's own regime fragment, this proposes no weights at all, so it carries none of
 * the "a target nobody asked for" concern that keeps `Optimiser`/`StressPanel` behind a button.
 * "Not enough history yet" is the ordinary state until 60 observations exist, not a fault - it
 * renders the same way `Gap` renders any other incomplete figure, not as an error.
 */
export function RegimeTile({ accountId }: { accountId: string }) {
  const regime = useRegime(accountId)

  if (regime.isError) {
    return (
      <StatCard label="Market regime">
        <Gap text="Could not load the market regime" />
      </StatCard>
    )
  }
  if (!regime.data) {
    return (
      <StatCard label="Market regime">
        <Pending query={regime} />
      </StatCard>
    )
  }

  const report = regime.data
  if (!report.available || !report.regime) {
    return (
      <StatCard label="Market regime">
        <Gap text={report.reason ?? 'Not enough history yet'} />
      </StatCard>
    )
  }

  const state = report.regime
  return (
    <StatCard label="Market regime">
      <div className="value">
        <span className={`badge ${state.state === 'STRESS' ? 'badge-custom' : 'badge-re'}`}>
          {state.state === 'STRESS' ? 'Stress' : 'Calm'}
        </span>
      </div>
      <div className="sub">
        Stress probability {pct(state.stressProbability)} · fitted to this book's own returns
      </div>
    </StatCard>
  )
}

/**
 * The coverage line under an AST-2 figure.
 *
 * <p>Rendered whenever the report is incomplete, and never suppressed to keep a tile tidy. The
 * report distinguishes the two gaps by direction: an unpriced holding understates net worth,
 * an unconvertible debt overstates it. The second is the one that flatters, so it is the one
 * that must not be quiet - it is marked as a warning rather than as an aside.
 */
export function Coverage({ report }: { report: NetWorthReport }) {
  if (report.complete) return null
  // A11. An absent cash balance overstates for the same reason an unconvertible debt does - the
  // missing figure is one that would have been subtracted - so it earns the same warning colour.
  // An unpriced holding understates, which is conservative and stays plain.
  const overstated = report.unconvertible.length > 0 || report.cash === null
  return (
    <div className={`sub ${overstated ? 'warn' : ''}`}>{report.reason}</div>
  )
}

export function NetWorthTile({ accountId }: { accountId: string }) {
  const netWorth = useNetWorth(accountId)

  if (netWorth.isError) {
    return (
      <StatCard label="Net worth">
        <Gap text="Could not load net worth" />
      </StatCard>
    )
  }
  if (!netWorth.data) {
    return (
      <StatCard label="Net worth">
        <Pending query={netWorth} />
      </StatCard>
    )
  }

  const report = netWorth.data
  return (
    <StatCard label="Net worth">
      <div className="value">{formatMoney(report.netWorth, report.currency)}</div>
      <div className="sub">
        {formatMoney(report.totalAssets, report.currency)} owned less{' '}
        {formatMoney(report.totalLiabilities, report.currency)} owed
      </div>
      {report.cash !== null && (
        // Named rather than folded into the line above: a margin loan is the difference between
        // what the positions are worth and what the account is worth, and it is the figure a
        // reader is most likely to be surprised by.
        <div className="sub">
          {formatMoney(report.cash, report.currency)} cash
          {report.cashAsOf ? ` as of ${report.cashAsOf}` : ''}
        </div>
      )}
      <Coverage report={report} />
    </StatCard>
  )
}

export function LiabilitiesTile({
  accountId,
  label = 'Liabilities (mortgage / debt)',
}: {
  accountId: string
  label?: string
}) {
  const netWorth = useNetWorth(accountId)

  if (netWorth.isError) {
    return (
      <StatCard label={label}>
        <Gap text="Could not load liabilities" />
      </StatCard>
    )
  }
  if (!netWorth.data) {
    return (
      <StatCard label={label}>
        <Pending query={netWorth} />
      </StatCard>
    )
  }

  const report = netWorth.data
  if (report.liabilitiesTotal === 0) {
    // Nothing recorded is a real, ordinary answer - and a different one from "not modelled",
    // which is what this tile said before AST-2 existed.
    return (
      <StatCard label={label}>
        <div className="value">{formatMoney('0', report.currency)}</div>
        <div className="sub">No debts recorded on this account</div>
      </StatCard>
    )
  }

  return (
    <StatCard label={label}>
      <div className="value">{formatMoney(report.totalLiabilities, report.currency)}</div>
      <div className="sub">
        {report.liabilitiesTotal} debt{report.liabilitiesTotal === 1 ? '' : 's'}
        {report.debtRatio != null ? ` · ${formatPercent(report.debtRatio)} of assets` : ''}
      </div>
      <Coverage report={report} />
    </StatCard>
  )
}

export function EquityTile({ accountId }: { accountId: string }) {
  const netWorth = useNetWorth(accountId)

  if (netWorth.isError) {
    return (
      <StatCard label="Equity">
        <Gap text="Could not load equity" />
      </StatCard>
    )
  }
  if (!netWorth.data) {
    return (
      <StatCard label="Equity">
        <Pending query={netWorth} />
      </StatCard>
    )
  }

  const report = netWorth.data
  return (
    <StatCard label="Equity">
      <div className="value">{formatMoney(report.netWorth, report.currency)}</div>
      <div className="sub">
        {report.equityRatio != null
          ? `${formatPercent(report.equityRatio)} of assets is yours`
          : 'No assets to be a share of'}
      </div>
      <Coverage report={report} />
    </StatCard>
  )
}

export function YieldTile({ accountId }: { accountId: string }) {
  const income = useQuery({
    queryKey: ['income', accountId],
    queryFn: () => api.income(accountId),
    placeholderData: (previous) => previous,
  })

  const report: IncomeReport | undefined = income.data

  if (income.isError) {
    return (
      <StatCard label="Yield on cost">
        <Gap text="Could not load income" />
      </StatCard>
    )
  }
  if (!report) {
    return (
      <StatCard label="Yield on cost">
        <Pending query={income} />
      </StatCard>
    )
  }
  if (!report.available || report.dividendYieldOnCost == null) {
    return (
      <StatCard label="Yield on cost">
        <Gap text={report.reason ?? 'No cost basis to yield on'} />
      </StatCard>
    )
  }

  return (
    <StatCard label="Yield on cost">
      <div className="value">{num(report.dividendYieldOnCost)}%</div>
      <div className="sub">
        {formatMoney(report.dividends, report.currency)} of dividends over 12 months
        {report.holdingsCovered < report.holdingsTotal
          ? ` · basis covers ${report.holdingsCovered} of ${report.holdingsTotal} holdings`
          : ''}
        {report.otherCurrencies.length > 0
          ? ` · ${report.otherCurrencies.join(', ')} income not included`
          : ''}
      </div>
    </StatCard>
  )
}
